import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import {
  XMarkIcon,
  CalendarIcon,
  VideoCameraIcon,
  MapPinIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  PaperAirplaneIcon,
  Cog6ToothIcon
} from '@heroicons/react/24/outline';
import { useLanguage } from '../../contexts/LanguageContext';
import { useNavigate } from 'react-router-dom';
import { showToast } from '../../utils/toast';
import { appointmentsAPI } from '../../utils/api';

const SendAppointmentModal = ({ isOpen, onClose, candidateData = {} }) => {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [bookingSettings, setBookingSettings] = useState(null);
  const [videoConferenceSettings, setVideoConferenceSettings] = useState(null);
  const [invitationContract, setInvitationContract] = useState(null);
  const [meetingType, setMeetingType] = useState(null); // null = let candidate choose, 'online' or 'physical'
  const [manualVideoUrl, setManualVideoUrl] = useState(false); // true if URL will be provided manually
  const [emailData, setEmailData] = useState({
    candidateName: '',
    candidateEmail: '',
    offerId: null
  });

  const fetchInvitationContract = useCallback(async (candidateSeed = {}) => {
    try {
      const response = await appointmentsAPI.getInvitationContract({
        candidateName: candidateSeed.name || '',
        candidateEmail: candidateSeed.email || '',
        meetingType,
        position: candidateSeed.position || '',
        offerId: candidateSeed.offerId || null,
        cvId: candidateSeed.cvId || candidateSeed.cv_id || null,
        manualVideoUrl: meetingType === 'online' && manualVideoUrl
      });
      setInvitationContract(response?.data || null);
    } catch (error) {
      setInvitationContract(null);
    }
  }, [meetingType, manualVideoUrl]);

  const fetchSettings = useCallback(async () => {
    try {
      const [bookingResult, videoResult] = await Promise.allSettled([
        appointmentsAPI.getBookingSettings(),
        appointmentsAPI.getVideoConferenceSettings()
      ]);

      if (bookingResult.status === 'fulfilled') {
        const data = bookingResult.value?.data || null;
        setBookingSettings(data?.settings || data);
      } else {
        setBookingSettings(null);
      }

      if (videoResult.status === 'fulfilled') {
        setVideoConferenceSettings(videoResult.value?.data || null);
      } else {
        setVideoConferenceSettings(null);
      }

      fetchInvitationContract(candidateData || {});
    } catch (error) {
      console.error('Error fetching settings:', error);
      setBookingSettings(null);
      setVideoConferenceSettings(null);
      setInvitationContract(null);
    }
  }, [candidateData, fetchInvitationContract]);

  useEffect(() => {
    if (isOpen) {
      fetchSettings();
    }
  }, [isOpen, fetchSettings]);

  useEffect(() => {
    // Update emailData when candidateData changes
    if (candidateData && isOpen) {
      setEmailData({
        candidateName: candidateData.name || '',
        candidateEmail: candidateData.email || '',
        offerId: candidateData.offerId || null
      });
      fetchInvitationContract(candidateData);
    }
  }, [candidateData, isOpen, fetchInvitationContract]);

  useEffect(() => {
    if (isOpen) {
      fetchInvitationContract(candidateData || {});
    }
  }, [isOpen, candidateData, fetchInvitationContract]);

  const getInvitationErrorMessage = (error) => {
    const code = error?.response?.data?.code;
    switch (code) {
      case 'APPOINTMENT_INVITATION_NO_BOOKING_URL':
        return t('appointments.sendModal.errors.bookingNotConfigured', 'Booking URL is not configured.');
      case 'APPOINTMENT_INVITATION_VIDEO_NOT_CONFIGURED':
        return t('appointments.sendModal.errors.noVideoConfig', 'Please configure video conferencing or provide a manual URL.');
      case 'APPOINTMENT_INVITATION_INVALID_EMAIL':
        return t('appointments.sendModal.errors.invalidEmail', 'Invalid candidate email.');
      case 'APPOINTMENT_INVITATION_INVALID_MEETING_TYPE':
        return t('appointments.sendModal.errors.invalidMeetingType', 'Invalid meeting type.');
      case 'APPOINTMENT_INVITATION_INVALID_OFFER_ID':
        return t('appointments.sendModal.errors.invalidOffer', 'Invalid offer selected.');
      case 'APPOINTMENT_INVITATION_INVALID_CV_ID':
        return t('appointments.sendModal.errors.invalidCv', 'Invalid CV selected.');
      case 'APPOINTMENT_INVITATION_MISSING_INFO':
        return t('appointments.sendModal.errors.missingInfo', 'Please fill in all required fields.');
      default:
        return error?.response?.data?.error
          || error?.response?.data?.message
          || error?.message
          || t('appointments.sendModal.errors.sendFailed', 'Error while sending invitation.');
    }
  };

  const handleSendInvitation = async () => {
    if (!emailData.candidateEmail || !emailData.candidateName) {
      showToast(t('appointments.sendModal.errors.missingInfo', 'Veuillez remplir tous les champs obligatoires'), 'error');
      return;
    }

    // Check if booking settings are properly configured
    if (!isBookingConfigured) {
      showToast(t('appointments.sendModal.errors.bookingNotConfigured', 'Vous devez d\'abord configurer et activer votre URL de réservation dans les paramètres'), 'error');
      return;
    }

    // Check if online meeting is selected but no video conference is configured and manual URL not selected
    if (meetingType === 'online' && !hasVideoConferenceConfigured() && !manualVideoUrl) {
      showToast(t('appointments.sendModal.errors.noVideoConfig', 'Veuillez cocher la case "Je fournirai l\'URL de la réunion manuellement" pour continuer'), 'error');
      return;
    }

    setLoading(true);
    
    try {
      const requestBody = {
        candidateName: emailData.candidateName,
        candidateEmail: emailData.candidateEmail,
        meetingType: meetingType,
        offerId: emailData.offerId,
        position: candidateData?.position || '',
        cvId: candidateData?.cvId || candidateData?.cv_id || null,
        manualVideoUrl: meetingType === 'online' && manualVideoUrl
      };

      await appointmentsAPI.sendInvitation(requestBody);

      showToast(t('appointments.sendModal.success', 'Invitation envoyée avec succès'), 'success');
      onClose();
    } catch (error) {
      console.error('Error sending invitation:', error);
      showToast(getInvitationErrorMessage(error), 'error');
    } finally {
      setLoading(false);
    }
  };

  const hasVideoConferenceConfigured = () => {
    if (videoConferenceSettings) {
      return Boolean(videoConferenceSettings.provider && videoConferenceSettings.is_active);
    }
    return Boolean(invitationContract?.context?.videoConfigured);
  };

  const getBookingUrl = () => {
    if (invitationContract?.context?.bookingUrl) {
      return invitationContract.context.bookingUrl;
    }
    if (!bookingSettings?.booking_url_slug) return '';
    // Use CLIENT_URL from environment or fallback to current origin
    const baseUrl = process.env.REACT_APP_CLIENT_URL || window.location.origin;
    return `${baseUrl}/rdv/${bookingSettings.booking_url_slug}`;
  };

  if (!isOpen) return null;
  const isBookingConfigured = Boolean(
    (bookingSettings?.is_active && bookingSettings?.booking_url_slug)
    || invitationContract?.context?.bookingConfigured
  );
  const isSendDisabled = loading || !isBookingConfigured || !emailData.candidateEmail || !emailData.candidateName;
  const optionCardClass = (isSelected) => [
    'rounded-xl border px-4 py-4 text-left transition-all duration-150',
    isSelected
      ? 'border-blue-500 bg-blue-50 shadow-sm ring-2 ring-blue-100'
      : 'border-slate-200 bg-white hover:border-slate-300 hover:bg-slate-50'
  ].join(' ');

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/55 p-4 backdrop-blur-sm"
      onClick={onClose}
    >
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.95 }}
        onClick={(event) => event.stopPropagation()}
        className="w-full max-w-3xl overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-2xl"
      >
        {/* Header */}
        <div className="border-b border-slate-200 bg-gradient-to-r from-blue-50 via-indigo-50 to-cyan-50 px-6 py-5">
          <div className="flex items-center justify-between">
            <div className="flex items-start gap-3">
              <div className="rounded-lg bg-white/80 p-2.5 shadow-sm ring-1 ring-blue-100">
                <CalendarIcon className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-slate-900">
                {t('appointments.sendModal.title', 'Envoyer une invitation de rendez-vous')}
                </h2>
                <p className="mt-0.5 text-sm text-slate-600">
                  {t('appointments.sendModal.subtitle', 'Préparez une invitation claire et professionnelle pour le candidat.')}
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="rounded-lg border border-slate-200 bg-white p-2 text-slate-500 transition hover:bg-slate-100 hover:text-slate-700"
              aria-label={t('common.close')}
            >
              <XMarkIcon className="h-5 w-5" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="max-h-[75vh] space-y-5 overflow-y-auto p-6">
          {/* Booking URL Info */}
          {bookingSettings ? (
            bookingSettings.is_active && bookingSettings.booking_url_slug ? (
              <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-4">
                <div className="flex items-start">
                  <CheckCircleIcon className="mr-2 h-5 w-5 text-emerald-600" />
                  <div className="flex-1">
                    <p className="text-sm font-semibold text-emerald-900">
                      {t('appointments.sendModal.bookingActive', 'Vos réservations sont activées')}
                    </p>
                    <p className="mt-1 text-sm text-emerald-800">
                      {t('appointments.sendModal.bookingUrl', 'URL de réservation')} :
                      <code className="ml-2 rounded bg-emerald-100 px-2 py-1 text-xs">
                        {getBookingUrl()}
                      </code>
                    </p>
                  </div>
                </div>
              </div>
            ) : (
              <div className="rounded-xl border border-red-200 bg-red-50 p-4">
                <div className="flex items-start">
                  <ExclamationTriangleIcon className="mr-2 h-5 w-5 text-red-600" />
                  <div className="flex-1">
                    <p className="text-sm font-semibold text-red-900">
                      {t('appointments.sendModal.bookingNotConfigured', 'URL de réservation non configurée')}
                    </p>
                    <p className="mt-1 text-sm text-red-800">
                      {t('appointments.sendModal.configureBookingUrl', 'Vous devez configurer et activer votre URL de réservation dans les paramètres avant d\'envoyer des invitations')}
                    </p>
                    <button
                      onClick={() => {
                        onClose();
                        navigate('/dashboard/rendez-vous');
                      }}
                      className="mt-3 inline-flex items-center rounded-lg border border-red-200 bg-white px-3 py-1.5 text-sm font-medium text-red-700 transition hover:bg-red-100"
                    >
                      {t('appointments.sendModal.goToSettings', 'Aller aux paramètres')}
                    </button>
                  </div>
                </div>
              </div>
            )
          ) : (
            <div className="rounded-xl border border-amber-200 bg-amber-50 p-4">
              <div className="flex items-start">
                <ExclamationTriangleIcon className="mr-2 h-5 w-5 text-amber-600" />
                <div>
                  <p className="text-sm font-medium text-amber-900">
                    {t('appointments.sendModal.loadingSettings', 'Chargement des paramètres...')}
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Meeting Type Selection */}
          <div className="rounded-xl border border-slate-200 bg-white p-5">
            <label className="mb-3 block text-sm font-semibold text-slate-800">
              {t('appointments.sendModal.meetingType', 'Type de rendez-vous')}
            </label>
            <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
              {/* Let candidate choose */}
              <button
                type="button"
                onClick={() => setMeetingType(null)}
                className={optionCardClass(meetingType === null)}
              >
                <div className="text-sm font-semibold text-slate-800">
                  {t('appointments.sendModal.letCandidateChoose', 'Laisser choisir')}
                </div>
                <p className="mt-1 text-xs text-slate-500">
                  {t('appointments.sendModal.candidateWillChoose', 'Le candidat choisira')}
                </p>
              </button>

              {/* Online */}
              <button
                type="button"
                onClick={() => setMeetingType('online')}
                className={optionCardClass(meetingType === 'online')}
              >
                <VideoCameraIcon className="mx-auto mb-2 h-6 w-6 text-slate-600" />
                <div className="text-sm font-semibold text-slate-800">
                  {t('appointments.sendModal.online', 'En ligne')}
                </div>
              </button>

              {/* Physical */}
              <button
                type="button"
                onClick={() => setMeetingType('physical')}
                className={optionCardClass(meetingType === 'physical')}
              >
                <MapPinIcon className="mx-auto mb-2 h-6 w-6 text-slate-600" />
                <div className="text-sm font-semibold text-slate-800">
                  {t('appointments.sendModal.physical', 'Physique')}
                </div>
              </button>
            </div>

            {/* Video Conference Options for Online Meeting */}
            {meetingType === 'online' && (
              <div className="mt-4">
                {!hasVideoConferenceConfigured() ? (
                  <div className="rounded-xl border border-amber-200 bg-amber-50 p-4">
                    <div className="flex items-start">
                      <ExclamationTriangleIcon className="mr-2 h-5 w-5 text-amber-600" />
                      <div className="flex-1">
                        <p className="text-sm font-semibold text-amber-900">
                          {t('appointments.sendModal.noVideoConfig', 'Aucun service de visioconférence configuré')}
                        </p>
                        <p className="mt-1 text-sm text-amber-800">
                          {t('appointments.sendModal.manualUrlRequired', 'Vous devrez fournir manuellement l\'URL de la réunion lors de la confirmation du rendez-vous.')}
                        </p>
                        <div className="mt-3">
                          <label className="flex items-center">
                            <input
                              type="checkbox"
                              checked={manualVideoUrl}
                              onChange={(e) => setManualVideoUrl(e.target.checked)}
                              className="rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                            />
                            <span className="ml-2 text-sm text-slate-700">
                              {t('appointments.sendModal.confirmManualUrl', 'Je fournirai l\'URL de la réunion manuellement')}
                            </span>
                          </label>
                        </div>
                        <div className="mt-2">
                          <button
                            onClick={() => {
                              onClose();
                              navigate('/dashboard/rendez-vous/video');
                            }}
                            className="inline-flex items-center rounded-lg border border-amber-200 bg-white px-3 py-1.5 text-sm font-medium text-amber-700 transition hover:bg-amber-100"
                          >
                            <Cog6ToothIcon className="h-4 w-4 mr-1" />
                            {t('appointments.sendModal.configureAuto', 'Configurer la génération automatique')}
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-4">
                    <div className="flex items-start">
                      <CheckCircleIcon className="mr-2 h-5 w-5 text-emerald-600" />
                      <div>
                        <p className="text-sm font-semibold text-emerald-900">
                          {t('appointments.sendModal.videoConfigured', 'Visioconférence configurée')}
                        </p>
                        <p className="mt-1 text-sm text-emerald-800">
                          {t('appointments.sendModal.autoUrlGeneration', 'L\'URL de la réunion sera générée automatiquement lors de la confirmation.')}
                        </p>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Candidate Information */}
          <div className="rounded-xl border border-slate-200 bg-white p-5">
            <h3 className="mb-4 text-sm font-semibold text-slate-800">
              {t('appointments.sendModal.candidateInformation', 'Informations du candidat')}
            </h3>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div>
              <label htmlFor="candidateName" className="mb-1 block text-sm font-medium text-slate-700">
                {t('appointments.sendModal.candidateName', 'Nom du candidat')} *
              </label>
              <input
                type="text"
                id="candidateName"
                value={emailData.candidateName}
                onChange={(e) => setEmailData({ ...emailData, candidateName: e.target.value })}
                  className="w-full rounded-xl border border-slate-300 px-3 py-2.5 text-sm text-slate-800 shadow-sm transition focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-100"
                placeholder={t('appointments.sendModal.candidateNamePlaceholder', 'Jean Dupont')}
              />
            </div>

            <div>
              <label htmlFor="candidateEmail" className="mb-1 block text-sm font-medium text-slate-700">
                {t('appointments.sendModal.candidateEmail', 'Email du candidat')} *
              </label>
              <input
                type="email"
                id="candidateEmail"
                value={emailData.candidateEmail}
                onChange={(e) => setEmailData({ ...emailData, candidateEmail: e.target.value })}
                  className="w-full rounded-xl border border-slate-300 px-3 py-2.5 text-sm text-slate-800 shadow-sm transition focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-100"
                placeholder={t('appointments.sendModal.candidateEmailPlaceholder', 'jean.dupont@email.com')}
              />
            </div>
            </div>
          </div>

          {/* Info about meeting type behavior */}
          <div className="rounded-xl border border-slate-200 bg-slate-50 p-4 text-sm text-slate-600">
            <p className="mb-2 font-semibold text-slate-700">
              {t('appointments.sendModal.infoTitle', 'Information importante :')}
            </p>
            <ul className="space-y-1 list-disc list-inside">
              <li>
                {t('appointments.sendModal.info1', 'Si vous choisissez le type de rendez-vous, le candidat ne pourra pas le modifier')}
              </li>
              <li>
                {t('appointments.sendModal.info2', 'Si vous laissez le candidat choisir, il pourra sélectionner entre en ligne et physique')}
              </li>
              <li>
                {t('appointments.sendModal.info3', 'Pour les rendez-vous en ligne, assurez-vous d\'avoir configuré un service de visioconférence')}
              </li>
            </ul>
          </div>
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-3 border-t border-slate-200 bg-slate-50 px-6 py-4">
          <button
            onClick={onClose}
            className="rounded-xl border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 transition hover:bg-slate-100"
          >
            {t('common.cancel', 'Annuler')}
          </button>
          <button
            onClick={handleSendInvitation}
            disabled={isSendDisabled}
            className="inline-flex items-center rounded-xl bg-blue-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {loading ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                {t('common.sending', 'Envoi...')}
              </>
            ) : (
              <>
                <PaperAirplaneIcon className="h-4 w-4 mr-2" />
                {t('appointments.sendModal.send', 'Envoyer l\'invitation')}
              </>
            )}
          </button>
        </div>
      </motion.div>
    </div>
  );
};

export default SendAppointmentModal;
